import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Slider from "react-slick";
import { LoadingScreen } from "Components";
import { CopyOfferLetter } from "./CopyOfferLetter";
import { newAuthService } from "Service/Axios";
import { formatDateWithShortMonth } from "Utils";
import {
  deleteOfferLetterUrl,
  getOfferLetterCategoryListUrl,
  getOfferLetterListByCategoryUrl,
  updateOfferLetterUrl,
} from "Constant/api";
import { paths } from "Constant/paths";

const settings = {
  dots: false,
  arrows: true,
  infinite: false,
  speed: 500,
  slidesToShow: 8,
  variableWidth: true,
  focusOnSelect: true,
  initialSlide: 0,
  responsive: [
    {
      breakpoint: 1024,
      settings: {
        slidesToShow: 6,
      },
    },
    {
      breakpoint: 600,
      settings: {
        slidesToShow: 4,
      },
    },
    {
      breakpoint: 576,
      settings: {
        slidesToShow: 3,
      },
    },
  ],
};

export default function OfferLetter() {
  const { t } = useTranslation();

  const defaultTab = "1";

  const tabSlderRef = useRef(null);

  const [offerLetterLoading, setOfferLetterLoading] = useState(true);
  const [copyOfferLetterModal, setCopyOfferLetterModal] = useState(false);
  const [offerLetterDetails, setOfferLetterDetails] = useState(null);
  const [activeTabId, setActiveTabId] = useState(defaultTab);
  const [activeTabName, setActiveTabName] = useState(defaultTab);
  const [tabList, setTabList] = useState([]);
  const [offerLetterList, setOfferLetterList] = useState([]);

  // Toggle
  const toggleOfferLetterModal = (copyOfferLetter) => {
    setCopyOfferLetterModal(!copyOfferLetterModal);
    if (copyOfferLetter) {
      setOfferLetterDetails(copyOfferLetter);
    } else {
      setOfferLetterDetails(null);
    }
  };

  //  Listing
  const getOfferLetterTabList = async (category) => {
    const payload = { type: category };
    const response = await newAuthService(
      getOfferLetterCategoryListUrl,
      "POST",
      payload
    );
    if (response.status) {
      setTabList(response.data);
      setActiveTabId(response.data?.[0]?.gid);
      tabSlderRef.current?.slickGoTo(0);
      setActiveTabName(response.data?.[0]?.type);
      getOfferLetterList(response.data?.[0]?.gid);
    } else {
      setTabList([]);
    }
  };

  const getOfferLetterList = async (category) => {
    setOfferLetterLoading(true);
    const payload = { type: category };
    const response = await newAuthService(
      getOfferLetterListByCategoryUrl,
      "POST",
      payload
    );
    if (response.status) {
      setOfferLetterList(response.data);
      setOfferLetterLoading(false);
    } else {
      setOfferLetterList([]);
      setOfferLetterLoading(false);
    }
  };

  useEffect(() => {
    getOfferLetterTabList();
  }, []);

  // Handle
  const onChangeTab = (tabId, tabName) => {
    setActiveTabId(tabId);
    setActiveTabName(tabName);
    getOfferLetterList(tabId);
  };

  const onDeleteOfferLetter = async (gid) => {
    const payload = {
      gid,
    };
    const response = await newAuthService(
      deleteOfferLetterUrl,
      "POST",
      payload
    );
    if (response.status) {
      getOfferLetterTabList();
      toast.success(
        t("common.messages.notification.success.deletedOfferLetter")
      );
    } else {
      toast.error(t("common.messages.notification.failed.somethingWentWrong"));
    }
  };

  const onFavouriteSubmit = async (
    offerLetter,
    favorite,
    archive,
    recylebin
  ) => {
    const payload = {
      gid: offerLetter.gid,
      name: offerLetter.name,
      category: offerLetter.category,
      jsondata: offerLetter.jsondata,
      html: offerLetter.html,
      favorite,
      archive,
      recylebin,
    };
    const response = await newAuthService(
      updateOfferLetterUrl,
      "POST",
      payload
    );
    if (response.status) {
      getOfferLetterTabList();
      toast.success(
        t(
          `common.messages.notification.success.${
            offerLetter.favorite
              ? "removeFavouriteOfferLetter"
              : "addFavouriteOfferLetter"
          }`
        )
      );
    } else {
      toast.error(response.message);
    }
  };

  return (
    <main className="main">
      <div className="card-white rounded-3 card-shadow">
        <div className="d-flex border-bottom">
          <div className="slide-status-nav dashboard-slidenav offer-slider w-100 border-0">
            <Slider {...settings} ref={tabSlderRef}>
              {tabList.map((tab) => (
                <div
                  className={`nav-status-item ${
                    tab.gid === activeTabId ? "active" : ""
                  }`}
                  key={tab.gid}
                >
                  <button onClick={() => onChangeTab(tab.gid, tab.type)}>
                    {tab.type}
                    <span className="badge badge-bg-perple text-purple">
                      {tab.count}
                    </span>
                  </button>
                </div>
              ))}
            </Slider>
          </div>
          <div className="ms-auto">
            <Link
              to={paths.admin.offerLetter.create}
              className="btn btn-secondary btn-sm text-nowrap"
            >
              <i className="bx bx-plus bx-sm align-middle"></i> Creat New
            </Link>
          </div>
        </div>

        <div className="d-flex align-items-center my-3"></div>

        <div className="table-container table-responsive min-h-250">
          <table className="table table-bordered">
            <thead>
              <tr>
                <th className="bg-slate-100">Name & Created By</th>
                <th className="bg-slate-100">Created</th>
                <th className="bg-slate-100">Participants</th>
                <th className="bg-slate-100">Last edited</th>
                <th width={140} className="bg-slate-100"></th>
              </tr>
            </thead>
            <tbody>
              {offerLetterLoading ? (
                <tr className="loading-screen">
                  <td colSpan={5}>
                    <LoadingScreen />
                  </td>
                </tr>
              ) : offerLetterList && offerLetterList.length > 0 ? (
                offerLetterList.map((offerLetter) => (
                  <tr key={offerLetter.gid}>
                    <td>
                      <h6 className="text-base text-slate-800">
                        {offerLetter.name}
                      </h6>
                      <Link
                        to={`${paths.admin.offerLetter.edit}/${offerLetter.gid}`}
                        className="d-inline-block mb-2 px-2 py-2 rounded bg-slate-100"
                      >
                        {offerLetter.createdBy}
                      </Link>
                    </td>
                    <td>
                      {offerLetter.creationDate
                        ? formatDateWithShortMonth(offerLetter.creationDate)
                        : ""}
                    </td>
                    <td>24</td>
                    <td>
                      {/* Oct 22, 2024 */}
                      {offerLetter.updationDate
                        ? formatDateWithShortMonth(offerLetter.updationDate)
                        : ""}
                    </td>
                    <td align="right">
                      <div className="d-flex justify-content-end align-items-center">
                        <button
                          className="border-0 text-orange-400 bg-orange-100 w-8 h-8 rounded"
                          onClick={() =>
                            onFavouriteSubmit(
                              offerLetter,
                              !offerLetter.favorite,
                              offerLetter.archive,
                              offerLetter.recylebin
                            )
                          }
                        >
                          <i
                            className={`bx bx${
                              offerLetter.favorite ? "s" : ""
                            }-star text-base`}
                          ></i>
                        </button>
                        <div className="dropdown">
                          <button
                            className="btn btn-transparent dropdown-toggle now-arrow"
                            type="button"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                          >
                            <i className="bx bx-dots-horizontal-rounded bx-sm"></i>
                          </button>
                          <ul className="dropdown-menu dropdown-menu-end">
                            <li>
                              <Link
                                to={`${paths.admin.offerLetter.edit}/${offerLetter.gid}`}
                                className="dropdown-item py-2 d-inline-block"
                              >
                                Edit
                              </Link>
                            </li>
                            <li>
                              <button
                                className="dropdown-item py-2 d-inline-block"
                                onClick={() =>
                                  onFavouriteSubmit(
                                    offerLetter,
                                    offerLetter.favorite,
                                    !offerLetter.archive,
                                    offerLetter.recylebin
                                  )
                                }
                              >
                                Archive
                              </button>
                            </li>
                            <li>
                              {activeTabName === "Recyle Bin" ? (
                                <button
                                  className="dropdown-item py-2 d-inline-block"
                                  onClick={() =>
                                    onDeleteOfferLetter(offerLetter.gid)
                                  }
                                >
                                  Permanent Delete
                                </button>
                              ) : (
                                <button
                                  className="dropdown-item py-2 d-inline-block"
                                  onClick={() =>
                                    onFavouriteSubmit(
                                      offerLetter,
                                      offerLetter.favorite,
                                      offerLetter.archive,
                                      !offerLetter.recylebin
                                    )
                                  }
                                >
                                  Move to Recyle Bin
                                </button>
                              )}
                            </li>
                            <li>
                              <button
                                className="dropdown-item py-2 d-inline-block"
                                onClick={() =>
                                  toggleOfferLetterModal(offerLetter)
                                }
                              >
                                Duplicate
                              </button>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5}>
                    <h4 className="text-center">No Data Found</h4>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {copyOfferLetterModal && (
        <CopyOfferLetter
          show={copyOfferLetterModal}
          data={offerLetterDetails}
          toggle={toggleOfferLetterModal}
          listing={() => getOfferLetterList(defaultTab)}
        />
      )}
    </main>
  );
}
